import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { getSetupWizardSessions } from '../api/setupWizardApi';
import { DataTable } from '../components/DataTable';
import { ErrorState } from '../components/ErrorState';
import { LoadingState } from '../components/LoadingState';
import { PageShell } from '../components/PageShell';
import { StatusBadge } from '../components/StatusBadge';

export function SetupWizardSessionsPage() {
  const { t } = useTranslation(['setupWizard', 'common', 'statuses']);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getSetupWizardSessions({ per_page: 100 });
      setRows(response.data || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const columns = useMemo(() => [
    {
      id: 'tenant_id',
      label: t('setupWizard:sessions.columns.tenant'),
      render: (row) => (
        <RouterLink to={`/tenants/${row.tenant_id}`}>
          {t('setupWizard:dashboard.tenant_link', { id: row.tenant_id })}
        </RouterLink>
      ),
    },
    {
      id: 'status',
      label: t('common:status'),
      render: (row) => <StatusBadge status={row.status} category="setup_session" />,
    },
    { id: 'current_step_code', label: t('setupWizard:sessions.columns.current_step') },
    {
      id: 'progress_percent',
      label: t('setupWizard:sessions.columns.progress'),
      render: (row) => `${Math.round(row.progress?.progress_percent ?? 0)}%`,
    },
    {
      id: 'required',
      label: t('setupWizard:sessions.columns.required'),
      render: (row) => (row.required ? t('statuses:yes') : t('statuses:no')),
    },
    { id: 'locale', label: t('setupWizard:sessions.columns.locale') },
    { id: 'started_at', label: t('setupWizard:sessions.columns.started') },
    { id: 'completed_at', label: t('setupWizard:sessions.columns.completed') },
  ], [t]);

  if (loading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState message={t('setupWizard:sessions.load_failed')} onRetry={load} />;
  }

  return (
    <PageShell title={t('setupWizard:sessions.title')} subtitle={t('setupWizard:sessions.subtitle')}>
      <DataTable columns={columns} rows={rows} rowKey="id" emptyMessage={t('setupWizard:sessions.no_sessions')} />
    </PageShell>
  );
}
